/**
 * useConsent composable
 *
 * Provides reactive consent state shared across all components.
 * The module-level refs act as a singleton — calling useConsent() in
 * multiple components returns the same reactive state, so banner and
 * modal stay in sync without a Pinia store.
 */

import { ref, readonly } from 'vue'
import {
  acceptAll as _acceptAll,
  rejectAll as _rejectAll,
  savePreferences as _savePreferences,
  hasDecided as _hasDecided,
  getStoredConsent
} from '@/services/consentService'

// Singleton refs — shared across all useConsent() callers
const _hasDecidedRef = ref(_hasDecided())
const _showModal = ref(false)
const _savedPrefs = ref(getStoredConsent())

function _sync() {
  _hasDecidedRef.value = _hasDecided()
  _savedPrefs.value = getStoredConsent()
}

export function useConsent() {
  function acceptAll() {
    _acceptAll()
    _sync()
    _showModal.value = false
  }

  function rejectAll() {
    _rejectAll()
    _sync()
    _showModal.value = false
  }

  function savePreferences(prefs) {
    _savePreferences(prefs)
    _sync()
    _showModal.value = false
  }

  function openPreferencesModal() {
    _showModal.value = true
  }

  function closePreferencesModal() {
    _showModal.value = false
  }

  return {
    hasDecided: readonly(_hasDecidedRef),
    showModal: _showModal,
    savedPrefs: readonly(_savedPrefs),
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferencesModal,
    closePreferencesModal
  }
}
